import { TCondition, type TFormField, type TValidation } from '@nirmata/core';
import { create } from 'zustand';

import type { ExampleItem } from '../components/fields/FormPreview';

export type DropTarget = 'above' | 'below' | 'inside';

interface BuilderStore {
  schema: TFormField | null;
  selectedField: string | null;
  fieldNames: string[];
  setSchema: (schema: TFormField) => void;
  setSelectedField: (fieldId: string | null) => void;
  addField: (field: TFormField, targetField?: string, position?: DropTarget) => void;
  updateField: (
    fieldId: string,
    key: string,
    value: string | Record<string, unknown> | TValidation[] | TCondition[]
  ) => void;
  removeField: (fieldId: string) => void;
  initializeContainer: () => void;
  // Added for example selection
  selectedExample: ExampleItem | null;
  setSelectedExample: (example: ExampleItem | null) => void;
  cachedSchema: TFormField | null;
  setCachedSchema: (schema: TFormField | null) => void;
}

// Helper to generate flat list of all fieldNames (no dot notation)
function getFieldNamesFromSchema(schema: TFormField): string[] {
  const result: string[] = [];
  function traverse(field: TFormField) {
    result.push(field.fieldName);
    if (Array.isArray(field.childFields)) {
      for (const child of field.childFields) {
        traverse(child);
      }
    }
  }
  traverse(schema);
  return result;
}

// Default container configuration
const DEFAULT_CONTAINER: TFormField = {
  fieldId: 'root_container',
  fieldName: 'root_container',
  componentType: 'container',
  componentProps: {
    label: 'Nirmata',
    size: 'default', // Add size prop to ensure padding is applied
  },
  fieldMetadata: {
    isInput: false,
    isContainer: true,
  },
  childFields: [],
};

export const useBuilderStore = create<BuilderStore>((set, get) => ({
  schema: null,
  selectedField: null,
  fieldNames: [],
  selectedExample: null,
  setSelectedExample: (example) => set({ selectedExample: example }),
  cachedSchema: null,
  setCachedSchema: (schema) => set({ cachedSchema: schema }),

  setSchema: (schema) => {
    set({
      schema,
      fieldNames: getFieldNamesFromSchema(schema),
    });
  },

  setSelectedField: (fieldId) => set({ selectedField: fieldId }),

  initializeContainer: () => {
    const { schema } = get();
    if (!schema) {
      set({
        schema: DEFAULT_CONTAINER,
        fieldNames: getFieldNamesFromSchema(DEFAULT_CONTAINER),
      });
    }
  },

  addField: (field, targetField, position = 'inside') =>
    set((state) => {
      // If no schema exists, create one with the new field
      if (!state.schema) {
        return { schema: field, fieldNames: getFieldNamesFromSchema(field) };
      }

      // If no target field specified, add to the root level
      if (!targetField) {
        let newSchema;
        if (!state.schema.childFields) {
          newSchema = {
            ...state.schema,
            childFields: [field],
          };
        } else {
          newSchema = {
            ...state.schema,
            childFields: [...state.schema.childFields, field],
          };
        }
        return {
          schema: newSchema,
          fieldNames: getFieldNamesFromSchema(newSchema),
        };
      }

      // Find the parent of the target field and the target field itself
      let targetParent: TFormField | null = null;
      let targetIndex = -1;

      const findTargetParentAndIndex = (
        parentField: TFormField | null,
        fields: TFormField[] = []
      ): boolean => {
        for (let i = 0; i < fields.length; i++) {
          const currentField = fields[i];
          if (currentField.fieldName === targetField) {
            targetParent = parentField;
            targetIndex = i;
            return true;
          }

          if (currentField.childFields && currentField.childFields.length > 0) {
            if (findTargetParentAndIndex(currentField, currentField.childFields)) {
              return true;
            }
          }
        }
        return false;
      };

      // Start search from root level
      if (state.schema.fieldName === targetField) {
        targetParent = state.schema;
        targetIndex = -1;
      } else if (state.schema.childFields) {
        findTargetParentAndIndex(state.schema, state.schema.childFields);
      }

      // Handle case where target field is the root schema
      if (targetField === state.schema.fieldName) {
        if (position === 'inside') {
          const newSchema = {
            ...state.schema,
            childFields: [...(state.schema.childFields || []), field],
          };
          return {
            schema: newSchema,
            fieldNames: getFieldNamesFromSchema(newSchema),
          };
        }
        // For root schema, above/below doesn't make sense, treat as inside
        const newSchema = {
          ...state.schema,
          childFields: [...(state.schema.childFields || []), field],
        };
        return {
          schema: newSchema,
          fieldNames: getFieldNamesFromSchema(newSchema),
        };
      }

      // Process based on position if a valid target was found
      if (targetParent && targetIndex !== -1) {
        // Ensure targetParent has childFields
        const targetParentChildFields = targetParent.childFields || [];

        // Create a copy of childFields we can modify
        const updatedChildren = [...targetParentChildFields];

        if (position === 'above' && targetIndex >= 0) {
          updatedChildren.splice(targetIndex, 0, field);
        } else if (position === 'below' && targetIndex >= 0) {
          updatedChildren.splice(targetIndex + 1, 0, field);
        } else if (position === 'inside' && targetIndex >= 0) {
          // Only add inside if the target supports children
          const targetField = updatedChildren[targetIndex];
          if (targetField && typeof targetField === 'object' && 'childFields' in targetField) {
            updatedChildren[targetIndex] = {
              ...targetField,
              childFields: [...(targetField.childFields || []), field],
            };
          } else {
            // If target doesn't support children, add below
            updatedChildren.splice(targetIndex + 1, 0, field);
          }
        }

        // Apply the updates recursively through the schema
        const updateChildFields = (currentField: TFormField): TFormField => {
          if (!currentField.childFields) return currentField;

          return {
            ...currentField,
            childFields: currentField.childFields.map((childField) => {
              if (targetParent && childField.fieldName === targetParent.fieldName) {
                return {
                  ...childField,
                  childFields: updatedChildren,
                };
              }
              return updateChildFields(childField);
            }),
          };
        };

        // If the target parent is the root schema
        if (targetParent.fieldName === state.schema.fieldName) {
          const updatedSchema = {
            ...state.schema,
            childFields: updatedChildren,
          };
          return {
            schema: updatedSchema,
            fieldNames: getFieldNamesFromSchema(updatedSchema),
          };
        }

        // Otherwise update the schema recursively
        const updatedSchema = updateChildFields(state.schema);
        return {
          schema: updatedSchema,
          fieldNames: getFieldNamesFromSchema(updatedSchema),
        };
      }

      // If no valid target found, append to root schema
      const newSchema = {
        ...state.schema,
        childFields: [...(state.schema.childFields || []), field],
      };
      return {
        schema: newSchema,
        fieldNames: getFieldNamesFromSchema(newSchema),
      };
    }),

  updateField: (fieldId, key, value) =>
    set((state) => {
      if (!state.schema) return state;

      const updateFieldRecursive = (field: TFormField): TFormField => {
        if (field.fieldId === fieldId) {
          if (key === 'fieldName') {
            return { ...field, [key]: value as string };
          } else if (key === 'validationRules') {
            return { ...field, validationRules: value as TValidation[] };
          } else if (key === 'componentProps') {
            return {
              ...field,
              componentProps: value as Record<string, unknown>,
            };
          } else if (key === 'conditionalRules') {
            return { ...field, conditionalRules: value as TCondition[] };
          } else {
            return {
              ...field,
              componentProps: {
                ...field.componentProps,
                [key]: value,
              },
            };
          }
        }

        if (field.childFields) {
          return {
            ...field,
            childFields: field.childFields.map(updateFieldRecursive),
          };
        }

        return field;
      };

      const updatedSchema = updateFieldRecursive(state.schema);
      return {
        schema: updatedSchema,
        fieldNames: updatedSchema ? getFieldNamesFromSchema(updatedSchema) : [],
      };
    }),

  removeField: (fieldId) =>
    set((state) => {
      if (!state.schema) return state;

      const removeFieldRecursive = (field: TFormField): TFormField | null => {
        if (field.fieldId === fieldId) {
          return null;
        }

        if (field.childFields) {
          const updatedChildren = field.childFields
            .map(removeFieldRecursive)
            .filter((f): f is TFormField => f !== null);

          return {
            ...field,
            childFields: updatedChildren,
          };
        }

        return field;
      };

      const updatedSchema = removeFieldRecursive(state.schema);
      return {
        schema: updatedSchema,
        selectedField: state.selectedField === fieldId ? null : state.selectedField,
        fieldNames: updatedSchema ? getFieldNamesFromSchema(updatedSchema) : [],
      };
    }),
}));
